/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Režňák (reznak@cadwork.cz)
 */

import WebGLRenderGraph from "App/render/webgl/render_graph";
import WebGLContext from "App/web_gl_context";
import Container from "App/utils/container";
import {Factory} from "App/utils/factory";
import View from "App/view/view";
import IScene from "js_interface/src/scene_interface";

export default
class Layer {
    protected _renderGraph: WebGLRenderGraph;
    protected _sceneAPI: IScene;
    protected _context: WebGLContext;
    protected _name: string;
    protected _framebuffers: Container;
    protected _passes: Container;
    protected _passesID: string | null;
    private _passesCreated: boolean;
    private _shaders: Container;
    protected _shadersID: string | null;
    private _shadersCreated: boolean;
    protected _fboReady: boolean;
    protected _shadersReady: boolean;
    protected _passesReady: boolean;

    constructor(aRenderGraph: WebGLRenderGraph) {
        this._renderGraph = aRenderGraph;

        this._sceneAPI = this._renderGraph.getSceneAPI();
        this._context = this._sceneAPI.getCanvas().getWebGLContext();

        this._name = 'Layer';
        this._framebuffers = new Container();

        this._passes = new Container();

        this._passesID = null;
        this._passesCreated = false;

        this._shaders = new Container();
        /** @type {string|null} */
        this._shadersID = null;
        this._shadersCreated = false;

        this._fboReady = false;
        this._shadersReady = false;
        this._passesReady = false;
    }

    public getShaders(): Container {
        this.initializeShaders();
        return this._shaders;
    }

    public getFramebuffers(): Container {
        this.initializeFramebuffers();
        return this._framebuffers;
    }

    public release() {
        this.invalidate();
    }

    /**
     * Create framebuffers
     *
     * @protected
     */
    protected _createFramebuffers() {
    }

    /**
     * Invalidate only framebuffers
     */
    public invalidateFramebuffers() {
        if (!this._fboReady) {
            return;
        }

        //logInfo(this._name + ' invalidateFramebuffers');
        this._fboReady = false;
        this._framebuffers.forEach('release');
    }

    /**
     * Invalidate only passes
     */
    public invalidatePasses() {
        if (!this._passesReady) {
            return;
        }

        //logInfo(this._name + ' invalidatePasses');
        this._passesReady = false;
        this._passes.forEach('release');
    }

    /**
     * Invalidate only shaders
     */
    public invalidateShaders() {
        if (!this._shadersReady) {
            return;
        }

        //logInfo(this._name + ' invalidateShaders');
        this._shadersReady = false;
        this._shaders.forEach('release');
    }

    /**
     * Invalidate this layer (for instance when you want to recreate framebuffers)
     */
    public invalidate() {
        this.invalidateFramebuffers();
        this.invalidatePasses();
        this.invalidateShaders();
    }

    public initializeFramebuffers() {
        if (this._fboReady) {
            return;
        }

        //logInfo(this._name + ' initializeFramebuffers');
        this._fboReady = true;
        this._createFramebuffers();
        this._framebuffers.forEach('initialize');
    }

    public initializeShaders() {
        if (this._shadersReady) {
            return;
        }

        //logInfo(this._name + ' initializeShaders');
        this._shadersReady = true;

        if (!this._shadersCreated && this._shadersID !== null) {
            //logInfo(this._name + ' creating new shaders');
            this._shadersCreated = true;
            this._shaders.fill(
                Factory.createAll(this._shadersID, this._sceneAPI, this._renderGraph));
        }

        this._shaders.forEach('initialize');
    }

    public initializePasses() {
        if (this._passesReady) {
            return;
        }

        //logInfo(this._name + ' initializePasses');
        this._passesReady = true;

        if (!this._passesCreated && this._passesID !== null) {
            //logInfo(this._name + ' creating new passes');
            this._passesCreated = true;
            this._passes.fill(
                Factory.createAll(this._passesID, this._sceneAPI, this._renderGraph));
        }

        this._passes.forEach('initialize');
    }

    /**
     * Recreate items
     */
    public initialize() {
        this.initializeFramebuffers();
        this.initializeShaders();
        this.initializePasses();
    }

    /**
     * @return {boolean} true if can render
     */
    public readyToRender(): boolean {
        if (this._sceneAPI.getSceneLoaded()) {
            this.initialize();
            return true;
        }

        return false;
    }

    /**
     * Render contents of this layer
     *
     * @param {View} aView
     * @return {boolean}
     */
    public render(aView: View): boolean {
        /* jshint unused:false */
        return true;
    }

    /**
     * Prepare some framebuffers before rendering
     *
     * @param {View} aView
     */
    public prerender(aView: View) {
        /* jshint unused:false */
    }

    /**
     * Called on canvas resize
     *
     * @param {View} aView
     */
    public onResize(aView: View) {

    }
}
